import { useCallback } from 'react';
import { useAuth } from '@/contexts';
import { savePost, unsavePost } from '@/api/post';
import { toast } from 'react-toastify';
import { debug, error } from '@/utils/logger';
import Save from '@/models/save';
import Post from '@/models/post';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_BOOKMARK_CLICKED } from '@/lib/analyticsEvents';

// Type for posts with save-related properties
// Using Partial<Post> to allow updates without requiring all Post properties
// Override saves to allow both Save instances and plain objects
export type PostWithSaves = Omit<Partial<Post>, 'saves'> & {
  _id: string;
  is_saved?: boolean;
  saves?: Array<{ user_id: string; post_id: string | number } | Save>;
};

// Type for updating arrays of Post or PostWithSaves
export type PostUpdateFunction = (
  updateFn: (prevPosts: (Post | PostWithSaves)[]) => (Post | PostWithSaves)[]
) => void;

/**
 * Custom hook for handling post save/unsave operations with authentication checks
 * and optimistic updates.
 * 
 * @returns Object with handleSave and handleUnsave functions
 */
export function usePostActions() {
  const { user } = useAuth();

  /**
   * Saves a post with optimistic UI updates
   */
  const handleSave = useCallback(
    async (postId: string, updatePosts: PostUpdateFunction) => {
      if (!user) {
        toast.error('Please log in to save posts.');
        return;
      }

      // Optimistic update: immediately show as saved
      updatePosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post._id === postId) {
            return {
              ...post,
              is_saved: true,
              saves: [
                ...(post.saves || []),
                { user_id: user._id, post_id: postId },
              ],
            } as typeof post;
          }
          return post;
        })
      );

      try {
        const result = await savePost(postId);
        debug('Post saved successfully:', result);

        // Track bookmark event
        WebAnalytics.trackContentInteraction(EVENT_BOOKMARK_CLICKED, {
          postID: postId,
          action: 'bookmark',
        });
      } catch (err) {
        error('Error saving post:', err);
        toast.error('Failed to save post. Please try again.');

        // Revert optimistic update on error
        // Remove the save entry we just added (user_id matches and post_id matches)
        updatePosts((prevPosts) =>
          prevPosts.map((post) => {
            if (post._id === postId) {
              return {
                ...post,
                is_saved: false,
                saves:
                  post.saves?.filter((save) => {
                    const saveUserId = save instanceof Save ? save.user_id : save.user_id;
                    return String(saveUserId) !== String(user._id);
                  }) || [],
              } as typeof post;
            }
            return post;
          })
        );
      }
    },
    [user]
  );

  /**
   * Unsaves a post with optimistic UI updates
   */
  const handleUnsave = useCallback(
    async (postId: string, updatePosts: PostUpdateFunction) => {
      if (!user) {
        toast.error('Please log in to unsave posts.');
        return;
      }

      // Optimistic update: immediately show as unsaved
      // Remove the save entry for this user (all saves in array are for this post)
      updatePosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post._id === postId) {
            const currentSaves = post.saves || [];
            const filteredSaves = currentSaves.filter((save) => {
              // Handle both Save instances and plain objects
              const saveUserId = save instanceof Save ? save.user_id : (save?.user_id ?? '');
              // Convert both to strings for reliable comparison
              return String(saveUserId) !== String(user._id);
            });
            
            return {
              ...post,
              is_saved: false,
              saves: filteredSaves,
            } as typeof post;
          }
          return post;
        })
      );

      try {
        const result = await unsavePost(postId);
        debug('Post unsaved successfully:', result);

        // Track unbookmark event
        WebAnalytics.trackContentInteraction(EVENT_BOOKMARK_CLICKED, {
          postID: postId,
          action: 'unbookmark',
        });
      } catch (err) {
        error('Error unsaving post:', err);
        toast.error('Failed to unsave post. Please try again.');

        // Revert optimistic update on error
        // User is guaranteed to exist here since we checked and returned early above
        updatePosts((prevPosts) =>
          prevPosts.map((post) => {
            if (post._id === postId) {
              return {
                ...post,
                is_saved: true,
                saves: [
                  ...(post.saves || []),
                  { user_id: user._id, post_id: postId },
                ],
              } as typeof post;
            }
            return post;
          })
        );
      }
    },
    [user]
  );

  /**
   * Handles save/unsave toggle - calls save or unsave based on current state
   */
  const handleToggleSave = useCallback(
    async (post: PostWithSaves, updatePosts: PostUpdateFunction) => {
      if (post.is_saved) {
        await handleUnsave(post._id, updatePosts);
      } else {
        await handleSave(post._id, updatePosts);
      }
    },
    [handleSave, handleUnsave]
  );

  return {
    handleSave,
    handleUnsave,
    handleToggleSave,
    isAuthenticated: !!user,
  };
}

